import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import type { StockStatus } from '@/features/catalog/api';
import { CatalogPage, type CatalogFilters } from '@/features/catalog/CatalogPage';

const stockValues: StockStatus[] = ['IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK'];

export interface CatalogSearch {
  /** Строка поиска: название, артикул, бренд. */
  q?: string;
  /** Идентификатор категории — фильтр списка. */
  category?: string;
  stock?: StockStatus;
}

/**
 * Фильтры каталога живут в адресе: ссылку на «мало осталось в семенах» нужно уметь переслать,
 * а возврат «назад» из карточки товара не должен сбрасывать отбор.
 *
 * Пустые значения из адреса вычищаются, чужие — отбрасываются: параметр может подставить кто
 * угодно, а `stock` уходит в запрос к API как есть.
 */
export const Route = createFileRoute('/_app/catalog')({
  validateSearch: (search: Record<string, unknown>): CatalogSearch => {
    const stock = search.stock;
    return {
      q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
      category: typeof search.category === 'string' && search.category !== '' ? search.category : undefined,
      stock: stockValues.find((value) => value === stock),
    };
  },
  component: CatalogRoute,
});

function CatalogRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Стабильная ссылка: экран держит на этом колбэке таймер задержки поиска, и новая функция
  // на каждый рендер сбрасывала бы отсчёт — строка уходила бы в адрес только после паузы.
  const onFiltersChange = useCallback(
    (next: Partial<CatalogFilters>) => {
      void navigate({
        search: (previous) => ({
          q: 'search' in next ? next.search || undefined : previous.q,
          category: 'categoryId' in next ? (next.categoryId ?? undefined) : previous.category,
          stock: 'stock' in next ? (next.stock ?? undefined) : previous.stock,
        }),
        // Правка фильтра — не шаг навигации: иначе «назад» отматывал бы по букве запроса.
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <CatalogPage
      filters={{
        search: search.q ?? '',
        categoryId: search.category ?? null,
        stock: search.stock ?? null,
      }}
      onFiltersChange={onFiltersChange}
    />
  );
}
